import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  findChildTicketKind,
  resolveChildTicketPoints,
} from '@cortex/domain-product-innovation-lab-basic-design-tree';
import { BACKLOG_BASE } from '../project/index.js';
import { ChildTicketKindLabel } from './ChildTicketKindLabel.js';
import type { BasicDesignExpansionHistoryItem } from '../../../lib/api/basic-design-expansion.js';

/**
 * 展開時刻を短い表記にする。秒まで出すのは同じ日に何度も展開した回を見分けるため。
 * @graph-connects none
 */
function formatExpandedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 履歴 1 回ぶんの項目を「ラベルの札」とその下の値で出す。札は地と字がテーマごとに反転するので、項目名と値が混ざって読めない形にならない
 * @graph-connects none
 */
function MetaItem({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="flex flex-col gap-1">
      <dt className="child-history-label">{label}</dt>
      <dd className="text-sm text-[var(--color-text-primary)]">{children}</dd>
    </div>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business カードの中で項目の区切りを作る。線 1 本とラベルの札だけで、見出しを大きくせずに段を分ける
 * @graph-connects none
 */
function CardSection({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="space-y-2 border-t border-[var(--color-border)] pt-3">
      <span className="child-history-label">{label}</span>
      {children}
    </div>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business チャンク 1 つの札。母票への行き先と pt・子票の増減を出し、その回に変わったチャンクだけ包みの色で目立たせる
 * @graph-connects none
 */
function ChunkChip({
  chunk,
}: {
  chunk: NonNullable<BasicDesignExpansionHistoryItem['chunks']>[number];
}): ReactNode {
  const { t } = useTranslation();
  // 古い記録の `changed` は API クライアント側で「変わった」に揃えている
  const changed = chunk.changed;
  const delta = [
    chunk.createdChildren > 0 ? `+${String(chunk.createdChildren)}` : '',
    chunk.removedChildren > 0 ? `-${String(chunk.removedChildren)}` : '',
  ]
    .filter((part) => part !== '')
    .join(' ');

  return (
    <li>
      <a
        href={`${BACKLOG_BASE}/view/${chunk.motohyoKey}`}
        target="_blank"
        rel="noreferrer"
        className={changed ? 'child-chunk-chip' : 'child-chunk-chip-quiet'}
        title={changed ? t('childTicketPlan.history.chunkChanged') : undefined}
      >
        <span className="truncate">
          {t('childTicketPlan.history.chunkOrder', { order: chunk.order })} {chunk.title}
        </span>
        <span className="font-mono tabular-nums opacity-80">{chunk.totalPoint}</span>
        {delta === '' ? null : <span className="font-mono text-[10px] tabular-nums">{delta}</span>}
      </a>
    </li>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business チャンクに分けて展開した回の内訳。展開済みチャンクを全部並べ、その回に変わったチャンクだけ色を変えて「どこを触った回か」を読めるようにする
 * @graph-connects ChunkChip [calls] チャンク 1 つの札
 */
function ChunkBreakdown({
  chunks,
}: {
  chunks: readonly NonNullable<BasicDesignExpansionHistoryItem['chunks']>[number][];
}): ReactNode {
  const { t } = useTranslation();
  const changedCount = chunks.filter((chunk) => chunk.changed).length;

  return (
    <div className="space-y-1.5">
      <p className="text-[11px] text-[var(--color-text-muted)]">
        {t('childTicketPlan.history.chunkChangedCount', { count: changedCount })}
      </p>
      {/* 並び順 = 統合プロダクト内リリース順。展開した回に送ったチャンクを全部出す */}
      <ul className="flex flex-wrap gap-1.5">
        {[...chunks]
          .sort((a, b) => a.order - b.order)
          .map((chunk) => (
            <ChunkChip key={chunk.chunkId} chunk={chunk} />
          ))}
      </ul>
    </div>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 展開済みの設計を読み取り専用の表で見せる。シートと同じ列順で並べ、行ごとに展開先の Backlog 課題へ飛べるようにする
 * @graph-connects resolveChildTicketPoints [calls] 自動計算ぶんを含む行ごとの pt
 * @graph-connects ChildTicketKindLabel [calls] 種別のラベル
 */
function ExpandedSheet({ entry }: { entry: BasicDesignExpansionHistoryItem }): ReactNode {
  const { t } = useTranslation();
  // 自動計算の種別は pt を持たないので、シートと同じ規則で導き直してから出す
  const points = useMemo(() => resolveChildTicketPoints(entry.rows), [entry.rows]);
  const issueByRowId = useMemo(
    () => new Map(entry.children.map((child) => [child.rowId, child.issueKey])),
    [entry.children],
  );

  if (entry.rows.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-[var(--color-border)] px-4 py-4 text-center text-xs text-[var(--color-text-muted)]">
        {t('childTicketPlan.history.noRows')}
      </p>
    );
  }

  // カードの中に置くので入れ子の枠は付けない（横に溢れる表だけ自分でスクロールさせる）
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] border-collapse text-sm">
        <thead>
          <tr className="bg-[var(--color-bg-elevated)] text-left text-[11px] text-[var(--color-text-muted)]">
            {(['kind', 'item', 'repository', 'content', 'point', 'assignee'] as const).map(
              (column) => (
                <th
                  key={column}
                  scope="col"
                  className={`px-3 py-2 font-medium ${column === 'point' ? 'text-right' : ''}`}
                >
                  {t(`childTicketPlan.columns.${column}`)}
                </th>
              ),
            )}
            <th scope="col" className="px-3 py-2 font-medium">
              {t('childTicketPlan.history.issue')}
            </th>
          </tr>
        </thead>
        <tbody>
          {entry.rows.map((row) => {
            const kind = findChildTicketKind(row.kindId);
            const issueKey = issueByRowId.get(row.rowId);
            return (
              <tr key={row.rowId} className="border-t border-[var(--color-border)] align-top">
                <td className="px-3 py-2">
                  {kind === undefined ? (
                    <span className="text-[var(--color-text-muted)]">{row.kindId}</span>
                  ) : (
                    <ChildTicketKindLabel label={kind.label} tone={kind.tone} size="sm" />
                  )}
                </td>
                <td className="px-3 py-2 text-[var(--color-text-secondary)]">{row.item ?? '—'}</td>
                <td className="px-3 py-2 text-[var(--color-text-secondary)]">
                  {row.repository ?? '—'}
                </td>
                <td className="px-3 py-2 whitespace-pre-wrap text-[var(--color-text-primary)]">
                  {row.content === '' ? '—' : row.content}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-[var(--color-text-primary)]">
                  {points.get(row.rowId) ?? 0}
                </td>
                <td className="px-3 py-2 text-[var(--color-text-secondary)]">
                  {row.assignee ?? '—'}
                </td>
                <td className="px-3 py-2">
                  {issueKey === undefined ? (
                    <span className="text-[var(--color-text-muted)]">—</span>
                  ) : (
                    <a
                      href={`${BACKLOG_BASE}${issueKey}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-mono text-xs text-[var(--color-accent-primary)] hover:underline"
                    >
                      {issueKey}
                    </a>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計タブの履歴表示。展開した回を新しい順に並べ、選んだ回の設計（行・pt・担当者・展開先の課題キー）を読み取り専用で見せる。編集はここからは出来ず、「再編集」で書き直した上での再展開に限る
 * @graph-connects ExpandedSheet [calls] 選んだ回の設計の表
 * @graph-connects formatExpandedAt [calls] 展開時刻の表記
 */
export function ChildTicketExpansionHistory({
  entries,
  onReEdit,
  reEditBlockedReason,
}: {
  /** 展開履歴（新しい順）。 */
  entries: readonly BasicDesignExpansionHistoryItem[];
  /** 再編集を押した。 */
  onReEdit: () => void;
  /**
   * 再編集に入れない理由。null なら入れる。
   *
   * チャンクに分けた案件は台帳からチャンクを組み直せないと編集を始められない（組み直さずに
   * 編集させると、チャンクに分けた設計を平らなシートで上書きすることになる）。
   */
  reEditBlockedReason: string | null;
}): ReactNode {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(0);
  const entry = entries[selected] ?? entries[0];
  if (entry === undefined) return null;

  return (
    <section className="animate-content-reveal space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('childTicketPlan.history.title')}
        </h3>
        <span className="rounded-full bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[11px] tabular-nums text-[var(--color-text-muted)]">
          {entries.length}
        </span>
        <button
          type="button"
          data-tone="primary"
          className="child-sheet-button ml-auto"
          disabled={reEditBlockedReason !== null}
          title={reEditBlockedReason ?? undefined}
          onClick={() => {
            onReEdit();
          }}
        >
          {t('childTicketPlan.history.reEdit')}
        </button>
      </div>

      {/* 回が 1 つだけなら切り替える先が無いので、選ぶ列は出さない */}
      {entries.length === 1 ? null : (
        <div className="flex flex-wrap gap-1.5">
          {entries.map((item, index) => (
            <button
              key={item.id ?? item.expandedAt}
              type="button"
              aria-pressed={index === selected}
              onClick={() => {
                setSelected(index);
              }}
              className={`rounded-full px-3 py-1 text-[11px] transition-colors ${
                index === selected
                  ? 'bg-[var(--color-accent-primary)] text-white'
                  : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
              }`}
            >
              {formatExpandedAt(item.expandedAt)}
            </button>
          ))}
        </div>
      )}

      {/* 1 回ぶんの項目は全部この 1 枚に入れる。回ごとに枠が増えると、どこまでが同じ回の
          話なのかが読めなくなる */}
      <div className="space-y-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-4 py-3">
        {entry.incomplete === true ? (
          <p
            className="rounded-lg px-3 py-2 text-xs"
            style={{
              background: 'color-mix(in srgb, var(--color-error) 12%, transparent)',
              color: 'var(--color-error)',
            }}
          >
            {t('childTicketPlan.history.incomplete')}
          </p>
        ) : null}

        {reEditBlockedReason === null ? null : (
          <p
            className="rounded-lg px-3 py-2 text-xs"
            style={{
              background: 'color-mix(in srgb, var(--color-error) 12%, transparent)',
              color: 'var(--color-error)',
            }}
          >
            {reEditBlockedReason}
          </p>
        )}

        {/* 値を札の下に積むので、横並びの折り返しでは列が揃わない。格子で受ける */}
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
          <MetaItem label={t('childTicketPlan.history.expandedAt')}>
            {formatExpandedAt(entry.expandedAt)}
          </MetaItem>
          <MetaItem label={t('childTicketPlan.history.expandedBy')}>{entry.expandedBy}</MetaItem>
          <MetaItem label={t('childTicketPlan.history.totalPoint')}>
            <span className="font-mono tabular-nums">{entry.totalPoint}</span>
          </MetaItem>
          <MetaItem label={t('childTicketPlan.history.basicDesignPoint')}>
            <span className="font-mono tabular-nums">{entry.basicDesignPoint}</span>
          </MetaItem>
          <MetaItem label={t('childTicketPlan.history.schedule')}>
            <span className="font-mono text-xs tabular-nums">
              {entry.startDate} → {entry.endDate}
            </span>
          </MetaItem>
          <MetaItem label={t('childTicketPlan.history.children')}>
            {t('childTicketPlan.history.sheets', { count: entry.children.length })}
          </MetaItem>
          {entry.removed.length === 0 ? null : (
            <MetaItem label={t('childTicketPlan.history.removed')}>
              {t('childTicketPlan.history.sheets', { count: entry.removed.length })}
            </MetaItem>
          )}
          {entry.retained.length === 0 ? null : (
            <MetaItem label={t('childTicketPlan.history.retained')}>
              {t('childTicketPlan.history.sheets', { count: entry.retained.length })}
            </MetaItem>
          )}
        </dl>

        {entry.chunks === undefined || entry.chunks.length === 0 ? null : (
          <CardSection label={t('childTicketPlan.history.chunks')}>
            <ChunkBreakdown chunks={entry.chunks} />
          </CardSection>
        )}

        <CardSection label={t('childTicketPlan.history.sheet')}>
          <ExpandedSheet entry={entry} />
        </CardSection>
      </div>

      <p className="text-[11px] text-[var(--color-text-muted)]">
        {t('childTicketPlan.history.lockedNote')}
      </p>
    </section>
  );
}
